import { getAssetKey } from '../util'

/**
 * @typedef {Object} Asset
 * @property {string} asset_id
 * @property {string} platform
 * @property {string} asset_class
 * @property {string} key
 */

/**
 * @typedef {Object} GroupedAssets
 * @property {Asset[]} adAccounts
 * @property {Asset[]} pages
 * @property {Asset[]} instagram
 * @property {Asset[]} pixels
 */

export const TestCampaignStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
}

export const TestCampaignStage = {
  CAMPAIGN: 'CAMPAIGN',
  ADSET: 'ADSET',
  AD: 'AD',
}

export const ErrorCategory = {
  PAYMENT: 'PAYMENT',
  PERMISSION: 'PERMISSION',
  UNKNOWN: 'UNKNOWN',
}

/**
 * Group assets by type for test campaign selection
 * @param {Asset[]} assets
 * @returns {GroupedAssets}
 */
export function getGroupedAssets(assets) {
  return {
    adAccounts: assets.filter((a) => getAssetKey(a) === 'facebook.ad_account' && a.is_enabled),
    pages: assets.filter((a) => getAssetKey(a) === 'facebook.profile' && a.is_enabled),
    instagram: assets.filter((a) => getAssetKey(a) === 'instagram.profile' && a.is_enabled),
    pixels: assets.filter((a) => getAssetKey(a) === 'facebook.pixel' && a.is_enabled),
  }
}

/**
 * Get available pages based on selected ad account
 * Pages are restricted to those connected to the selected ad account
 * @param {Asset[]} assets
 * @param {Asset|null} selectedAdAccount
 * @returns {Asset[]}
 */
export function getAvailablePages(assets, selectedAdAccount) {
  const allPages = assets.filter((a) => getAssetKey(a) === 'facebook.profile' && a.is_enabled)

  if (!selectedAdAccount) {
    return []
  }

  return allPages.filter((page) =>
    page.connected_assets?.some(
      (connected) =>
        connected.asset_id === selectedAdAccount.asset_id &&
        connected.asset_class === 'ad_account' &&
        connected.platform === 'facebook' &&
        connected.is_active,
    ),
  )
}

/**
 * Get available Instagram accounts based on selected ad account and/or page
 * Instagram accounts are restricted to those connected to the selected ad account or page
 * @param {Asset[]} assets
 * @param {Asset|null} selectedAdAccount
 * @param {Asset|null} selectedPage
 * @returns {Asset[]}
 */
export function getAvailableInstagramAccounts(assets, selectedAdAccount, selectedPage) {
  const allInstagram = assets.filter((a) => getAssetKey(a) === 'instagram.profile' && a.is_enabled)

  if (!selectedAdAccount && !selectedPage) {
    return []
  }

  return allInstagram.filter((instagram) =>
    instagram.connected_assets?.some(
      (connected) =>
        (selectedAdAccount &&
          connected.asset_id === selectedAdAccount.asset_id &&
          connected.asset_class === 'ad_account' &&
          connected.platform === 'facebook' &&
          connected.is_active) ||
        (selectedPage &&
          connected.asset_id === selectedPage.asset_id &&
          connected.asset_class === 'profile' &&
          connected.platform === 'facebook' &&
          connected.is_active),
    ),
  )
}

/**
 * Get available pixels based on selected ad account
 * Pixels are restricted to those connected to the selected ad account
 * @param {Asset[]} assets
 * @param {Asset|null} selectedAdAccount
 * @returns {Asset[]}
 */
export function getAvailablePixels(assets, selectedAdAccount) {
  const allPixels = assets.filter((a) => getAssetKey(a) === 'facebook.pixel' && a.is_enabled)

  if (!selectedAdAccount) {
    return []
  }

  return allPixels.filter((pixel) =>
    pixel.connected_assets?.some(
      (connected) =>
        connected.asset_id === selectedAdAccount.asset_id &&
        connected.asset_class === 'ad_account' &&
        connected.platform === 'facebook' &&
        connected.is_active,
    ),
  )
}

/**
 * Check if test campaign button should be enabled
 * @param {Asset[]} assets
 * @returns {boolean}
 */
export function canEnableTestCampaign(assets) {
  const grouped = getGroupedAssets(assets)
  return grouped.adAccounts.length >= 1 && grouped.pages.length >= 1
}

/**
 * Validate asset selection for test campaign
 * @param {Object} selectedAssets
 * @param {Asset|null} selectedAssets.adAccount
 * @param {Asset|null} selectedAssets.page
 * @param {Asset|null} selectedAssets.instagram
 * @param {Asset|null} selectedAssets.pixel
 * @returns {boolean}
 */
export function validateAssetSelection(selectedAssets) {
  return !!selectedAssets.adAccount && !!selectedAssets.page
}

/**
 * Generate Meta Ads Manager URL for a campaign
 * @param {string} adAccountId
 * @param {string} campaignId
 * @returns {string}
 */
export function getAdsManagerUrl(adAccountId, campaignId) {
  const actId = adAccountId.replace('act_', '')
  const filterSet = `SEARCH_BY_CAMPAIGN_GROUP_IDS-STRING_SET%1EANY%1E[%22${campaignId}%22]`
  return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${actId}&filter_set=${filterSet}`
}

/**
 * Get polling interval based on elapsed time
 * @param {number} elapsedMs
 * @returns {number}
 */
export function getPollingInterval(elapsedMs) {
  if (elapsedMs < 60000) return 5000
  if (elapsedMs < 180000) return 1000
  if (elapsedMs < 360000) return 15000
  return 30000
}

/**
 * Check if campaign is in a terminal state
 * @param {string} status
 * @returns {boolean}
 */
export function isTerminalState(status) {
  if (!status) return false
  return status === TestCampaignStatus.SUCCESS || status === TestCampaignStatus.FAILURE
}

/**
 * Get stage display order
 * @param {string} stage
 * @returns {number}
 */
export function getStageOrder(stage) {
  const order = {
    [TestCampaignStage.CAMPAIGN]: 1,
    [TestCampaignStage.ADSET]: 2,
    [TestCampaignStage.AD]: 3,
  }
  return order[stage] || 0
}
